import { useContext, useState, useMemo, useCallback } from "react";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { ActividadContext } from "../../../../context/ActividadContext";
import { savePago, updatePago, deletePago } from "../../../../application/pagos";
import { saveGasto, deleteGasto } from "../../../../application/gastos";
import { useSortableData } from "../../../../hooks/useSortableData";
import SortableHeader from "../../../../components/SortableHeader";
import MoneyInput from "../../../../components/MoneyInput";
import ReciboShareModal from "../../../../components/Recibo/ReciboShareModal";
import {
  formatMonto,
  estadoVencimiento,
  diasHastaFecha,
  calcularComision,
  formatFechaCorta as fmtDate,
} from "../../../../utils/rentalCalculations";
import { parseLocalDate, calcularFactorIPC, calcularFactorICL } from "../../../../utils/ipcCalculations";
import { getVencimientoClasses } from "../../../../utils/rentalStatusColors";
import { nombreCompleto } from "../../../../utils/nombre";
import { agruparPorClave } from "../../../../utils/agrupar";
import { montoEnPeriodo } from "../../../../utils/montoHistorico";
import {
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationCircle,
  FaTimes,
  FaHistory,
  FaMoneyBillWave,
  FaTrash,
  FaWrench,
  FaArrowUp,
  FaFileExcel,
  FaFilePdf,
  FaReceipt,
} from "react-icons/fa";

// Frecuencia -> meses, para calcular el periodo del ajuste hacia atras.
const FREQ_MESES = {
  TRIMESTRAL: 3,
  CUATRIMESTRAL: 4,
  SEMESTRAL: 6,
  ANUAL: 12,
};

// Same projection convention as Aumentos.jsx (months in [desde, hasta), missing
// IPC months filled with the average of published ones). Returns the result
// object from calcularFactorIPC/ICL or null.
const factorAcumulado = (indices, tipo, fechaDesde, fechaHasta) => {
  if (!tipo || !indices?.length) return null;
  const filtrados = indices.filter((i) => i.tipo === tipo);
  if (!filtrados.length) return null;
  return tipo === "ICL"
    ? calcularFactorICL(filtrados, fechaDesde, fechaHasta)
    : calcularFactorIPC(filtrados, fechaDesde, fechaHasta);
};

const CATEGORIAS = [
  "Plomeria", "Electricidad", "Pintura", "Cerrajeria", "Limpieza",
  "Gas", "Reparacion general", "Expensas", "Impuestos", "Otro",
];

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function Cobros() {
  const { contratosActivos, locadores, locatarios, pagos, gastos, indices, config, aumentosAplicados, refreshPagos, refreshGastos } = useContext(AlquileresContext);
  const { registrarActividad } = useContext(ActividadContext);
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [search, setSearch] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("TODOS");
  const [showExtras, setShowExtras] = useState(false);
  const [agrupar, setAgrupar] = useState(false);
  const [modalContratoId, setModalContratoId] = useState(null);
  const [modalHistorial, setModalHistorial] = useState(null);
  const [reciboData, setReciboData] = useState(null);
  const [pagoForm, setPagoForm] = useState({ montoPagado: "", observaciones: "" });
  const [gastoForm, setGastoForm] = useState({ descripcion: "", monto: "", categoria: "Reparacion general" });
  const [showGastoForm, setShowGastoForm] = useState(false);
  const [saving, setSaving] = useState(false);

  // Build payment status for each active contract this month
  const contratosConPago = useMemo(() => {
    const diasAlerta = config?.diasAlertaVencimiento || 30;
    const comisionDefault = config?.comisionPorcentaje || 5;
    const nombrePorId = (lista) => {
      const m = {};
      lista.forEach((p) => { if (p.idFirebase) m[p.idFirebase] = nombreCompleto(p); });
      return m;
    };
    const locadorMap = nombrePorId(locadores);
    const locatarioMap = nombrePorId(locatarios);
    return contratosActivos.map((c) => {
      const pago = pagos.find(
        (p) => p.contratoId === c.idFirebase && p.mes === mes && p.anio === anio
      );
      const gastosContrato = gastos.filter(
        (g) => g.contratoId === c.idFirebase && g.mes === mes && g.anio === anio
      );
      const totalGastos = gastosContrato.reduce((s, g) => s + (Number(g.monto) || 0), 0);
      // Monto vigente en el periodo elegido, no el monto actual del contrato:
      // si el alquiler aumento en septiembre, agosto se sigue viendo al precio
      // viejo.
      const montoContratoActual = Number(c.montoActual) || 0;
      const { monto: montoPeriodo, esHistorico: montoEsHistorico } = montoEnPeriodo(c, anio, mes, {
        aumentosAplicados,
        pago,
      });
      const montoNeto = montoPeriodo - totalGastos;
      let estado = "PENDIENTE";
      if (pago) {
        if (Number(pago.montoPagado) >= montoNeto) {
          estado = "PAGADO";
        } else {
          estado = "PARCIAL";
        }
      }

      // Comisión inmobiliaria (la que se queda la inmobiliaria)
      const comisionPct = Number(c.comisionPorcentaje) || comisionDefault;
      const comisionMonto = montoEsHistorico
        ? calcularComision(montoPeriodo, comisionPct)
        : Number(c.comisionMonto) || calcularComision(montoPeriodo, comisionPct);
      // Total que recibe el propietario (sin descontar gastos): MONTO - ADM
      const totalPropietario = montoPeriodo - comisionMonto;

      // Vencimiento del contrato
      const venceEstado = estadoVencimiento(c.fechaVencimiento, diasAlerta);
      const diasVence = diasHastaFecha(c.fechaVencimiento);

      // Próximo aumento
      const proximoAum = c.aumento?.proximoAumento || "";
      const diasAumento = diasHastaFecha(proximoAum);

      // Monto proyectado tras el próximo aumento. El periodo del ajuste es
      // siempre la frecuencia hacia atras desde proximoAumento (igual que en
      // Aumentos.jsx). Usar fechaInicio o fechaUltimoAumento del importer
      // sobre-cuenta meses cuando el contrato ya tuvo aumentos no registrados.
      let montoProyectado = null;
      let factorProyectado = null;
      let proyeccionAprox = false;
      const tipoIndice = c.aumento?.tipo;
      const frecuencia = c.aumento?.frecuencia;
      const mesesFrec = FREQ_MESES[frecuencia] || 4;
      if (tipoIndice && proximoAum) {
        const hasta = parseLocalDate(proximoAum);
        if (hasta) {
          const desde = new Date(hasta);
          desde.setMonth(desde.getMonth() - mesesFrec);
          const res = factorAcumulado(indices, tipoIndice, desde, hasta);
          if (res && res.factor > 1) {
            factorProyectado = res.factor;
            proyeccionAprox = res.aproximado || false;
            montoProyectado = Math.round(montoContratoActual * res.factor);
          }
        }
      }

      return {
        ...c,
        montoActual: montoPeriodo,
        montoContratoActual,
        montoEsHistorico,
        locadorNombre: c.locadorNombre || locadorMap[c.locadorId] || "",
        locatarioNombre: c.locatarioNombre || locatarioMap[c.locatarioId] || "",
        pago,
        estadoPago: estado,
        gastosContrato,
        totalGastos,
        montoNeto,
        comisionMontoCalc: comisionMonto,
        comisionPct,
        totalPropietario,
        venceEstado,
        diasVence,
        diasAumento,
        montoProyectado,
        factorProyectado,
        proyeccionAprox,
      };
    });
  }, [contratosActivos, locadores, locatarios, pagos, gastos, indices, config, aumentosAplicados, mes, anio]);

  const filtered = useMemo(() => {
    return contratosConPago.filter((c) => {
      const matchSearch =
        !search ||
        c.locatarioNombre?.toLowerCase().includes(search.toLowerCase()) ||
        c.locadorNombre?.toLowerCase().includes(search.toLowerCase()) ||
        c.propiedad?.toLowerCase().includes(search.toLowerCase());
      const matchEstado = filtroEstado === "TODOS" || c.estadoPago === filtroEstado;
      return matchSearch && matchEstado;
    });
  }, [contratosConPago, search, filtroEstado]);

  const getSortValue = useCallback((c, key) => {
    if (key === "proximoAumento") return c.aumento?.proximoAumento;
    if (key === "montoPagado") return c.pago?.montoPagado;
    if (key === "fechaPago") return c.pago?.fechaPago;
    return c?.[key];
  }, []);
  const { sortedData, sortConfig, requestSort } = useSortableData(filtered, {
    getValue: getSortValue,
    storageKey: "sort-cobros",
    initial: { key: "estadoPago", direction: "asc" },
  });

  // Filas a renderizar. Con agrupado, intercala una fila de encabezado por
  // locador (con subtotales) antes de sus contratos.
  const filasRender = useMemo(() => {
    if (!agrupar) return sortedData.map((c) => ({ tipo: "fila", c }));
    const grupos = agruparPorClave(sortedData, (c) => c.locadorNombre);
    const out = [];
    for (const [locador, items] of grupos) {
      out.push({
        tipo: "grupo",
        locador,
        items,
        subtotalMonto: items.reduce((s, x) => s + (Number(x.montoActual) || 0), 0),
        subtotalCobrado: items.reduce((s, x) => s + (x.pago ? Number(x.pago.montoPagado) || 0 : 0), 0),
      });
      items.forEach((c) => out.push({ tipo: "fila", c }));
    }
    return out;
  }, [agrupar, sortedData]);

  const totales = useMemo(() => {
    const total = contratosConPago.length;
    const pagados = contratosConPago.filter((c) => c.estadoPago === "PAGADO").length;
    const parciales = contratosConPago.filter((c) => c.estadoPago === "PARCIAL").length;
    const pendientes = contratosConPago.filter((c) => c.estadoPago === "PENDIENTE").length;
    const montoEsperado = contratosConPago.reduce((s, c) => s + (Number(c.montoActual) || 0), 0);
    const montoCobrado = contratosConPago.reduce((s, c) => {
      return s + (c.pago ? Number(c.pago.montoPagado) || 0 : 0);
    }, 0);
    return { total, pagados, parciales, pendientes, montoEsperado, montoCobrado };
  }, [contratosConPago]);

  const handleExportarExcel = async () => {
    const XLSX = await import("xlsx");
    const data = filtered.map((c) => ({
      Estado: c.estadoPago,
      Locatario: c.locatarioNombre || "",
      Locador: c.locadorNombre || "",
      Propiedad: c.propiedad || "",
      Monto: Number(c.montoActual) || 0,
      "%": Number(c.comisionPct) || 0,
      ADM: Number(c.comisionMontoCalc) || 0,
      Total: Number(c.totalPropietario) || 0,
      Vence: c.fechaVencimiento ? fmtDate(c.fechaVencimiento) : "",
      "Dias vence": c.diasVence ?? "",
      Aumento: c.aumento?.proximoAumento ? fmtDate(c.aumento.proximoAumento) : "",
      Indice: c.aumento?.tipo || "",
      Frecuencia: c.aumento?.frecuenciaMeses ? `${c.aumento.frecuenciaMeses}m` : "",
      "Mto. ajustado": c.montoProyectado || "",
      Gastos: Number(c.totalGastos) || 0,
      Pagado: c.pago ? Number(c.pago.montoPagado) : "",
      "Fecha Pago": c.pago?.fechaPago ? fmtDate(c.pago.fechaPago) : "",
      Observaciones: c.pago?.observaciones || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [
      { wch: 10 }, { wch: 25 }, { wch: 30 }, { wch: 28 }, { wch: 12 },
      { wch: 5 },  { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 9 },
      { wch: 10 }, { wch: 8 },  { wch: 10 }, { wch: 14 }, { wch: 10 },
      { wch: 12 }, { wch: 11 }, { wch: 30 },
    ];
    const wb = XLSX.utils.book_new();
    const sheetName = `${MESES[mes - 1]} ${anio}`;
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `Cobros_${MESES[mes - 1]}_${anio}.xlsx`);
  };

  // Always get fresh data for the open modal
  const modalContrato = useMemo(() => {
    if (!modalContratoId) return null;
    return contratosConPago.find((c) => c.idFirebase === modalContratoId) || null;
  }, [modalContratoId, contratosConPago]);

  // Monto neto = alquiler - gastos del propietario
  const montoNetoModal = modalContrato
    ? Number(modalContrato.montoActual) - (modalContrato.totalGastos || 0)
    : 0;

  const openRegistrarPago = (contrato) => {
    setModalContratoId(contrato.idFirebase);
    const neto = Number(contrato.montoActual) - (contrato.totalGastos || 0);
    setPagoForm({
      montoPagado: contrato.pago ? contrato.pago.montoPagado : neto,
      observaciones: contrato.pago ? contrato.pago.observaciones || "" : "",
    });
    setGastoForm({ descripcion: "", monto: "", categoria: "Reparacion general" });
    setShowGastoForm(false);
  };

  // Update suggested amount when gastos change
  // When gastos change and modal is open, update the suggested monto
  useMemo(() => {
    if (!modalContrato || modalContrato.pago) return;
    const neto = Number(modalContrato.montoActual) - (modalContrato.totalGastos || 0);
    setPagoForm((prev) => ({ ...prev, montoPagado: neto }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalContrato?.totalGastos, modalContrato?.idFirebase]);

  const handleAgregarGasto = async () => {
    if (!gastoForm.descripcion || !gastoForm.monto || !modalContrato) return;
    setSaving(true);
    try {
      await saveGasto({
        contratoId: modalContrato.idFirebase,
        locadorNombre: modalContrato.locadorNombre,
        locatarioNombre: modalContrato.locatarioNombre,
        propiedad: modalContrato.propiedad,
        locadorId: modalContrato.locadorId || "",
        descripcion: gastoForm.descripcion,
        monto: Number(gastoForm.monto),
        fecha: new Date().toISOString().split("T")[0],
        mes,
        anio,
        categoria: gastoForm.categoria,
        pagadoPor: "PROPIETARIO",
      });
      await refreshGastos();
      setGastoForm({ descripcion: "", monto: "", categoria: "Reparacion general" });
      setShowGastoForm(false);
    } catch (error) {
      console.error("Error al agregar gasto:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarGasto = async (gastoId) => {
    try {
      await deleteGasto(gastoId);
      await refreshGastos();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // Datos del recibo de cobro a partir del contrato + el pago registrado.
  const buildRecibo = (contrato, pago) => ({
    nombre: contrato.locatarioNombre,
    propiedad: contrato.propiedad,
    mes: pago.mes,
    anio: pago.anio,
    monto: Number(pago.montoPagado),
    moneda: pago.moneda || contrato.moneda || "ARS",
    fecha: pago.fechaPago,
    observaciones: pago.observaciones || "",
    telefono:
      locatarios.find((l) => l.idFirebase === contrato.locatarioId)?.telefono || "",
    firmaImg: config?.firmaImg || "",
    aclaracion: config?.firmaAclaracion || "",
    domicilio: config?.reciboDomicilio || "",
    localidad: config?.reciboLocalidad || "",
    cuit: config?.reciboCuit || "",
  });

  const handleRegistrarPago = async () => {
    if (!modalContrato) return;
    setSaving(true);
    try {
      const pagoData = {
        contratoId: modalContrato.idFirebase,
        locatarioNombre: modalContrato.locatarioNombre,
        locadorNombre: modalContrato.locadorNombre,
        propiedad: modalContrato.propiedad,
        moneda: modalContrato.moneda || "ARS",
        mes,
        anio,
        montoEsperado: Number(modalContrato.montoActual),
        montoPagado: Number(pagoForm.montoPagado),
        fechaPago: new Date().toISOString().split("T")[0],
        observaciones: pagoForm.observaciones,
      };

      if (modalContrato.pago) {
        await updatePago(modalContrato.pago.idFirebase, pagoData);
      } else {
        await savePago(pagoData);
      }
      await refreshPagos();
      setModalContratoId(null);
      // Ofrecer el recibo para compartir por WhatsApp recién registrado el pago.
      setReciboData(buildRecibo(modalContrato, pagoData));
      registrarActividad("COBRO_ALQUILER", {
        mensaje: `Cobro registrado: ${modalContrato.locatarioNombre} - ${modalContrato.propiedad} - ${mes}/${anio}`,
        entidadTipo: "CONTRATO",
        entidadId: modalContrato.idFirebase,
        entidadLabel: modalContrato.propiedad,
        monto: pagoData.montoPagado,
        moneda: pagoData.moneda,
      });
    } catch (error) {
      console.error("Error al registrar pago:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleAnularPago = async (pagoId) => {
    if (!window.confirm("¿Anular este pago?")) return;
    try {
      const pago = pagos.find((p) => p.idFirebase === pagoId);
      await deletePago(pagoId);
      await refreshPagos();
      if (pago) {
        registrarActividad("PAGO_ANULADO", {
          mensaje: `Pago anulado: ${pago.locatarioNombre} - ${pago.propiedad} - ${pago.mes}/${pago.anio}`,
          entidadTipo: "CONTRATO",
          entidadId: pago.contratoId,
          entidadLabel: pago.propiedad,
          monto: pago.montoPagado,
          moneda: pago.moneda,
        });
      }
    } catch (error) {
      console.error("Error al anular pago:", error);
    }
  };

  // Historial de pagos de un contrato
  const openHistorial = (contrato) => {
    const historial = pagos
      .filter((p) => p.contratoId === contrato.idFirebase)
      .sort((a, b) => {
        if (a.anio !== b.anio) return b.anio - a.anio;
        return b.mes - a.mes;
      });
    setModalHistorial({ contrato, historial });
  };

  const estadoIcon = (estado) => {
    switch (estado) {
      case "PAGADO":
        return <FaCheckCircle className="text-green-500 w-5 h-5" />;
      case "PARCIAL":
        return <FaExclamationCircle className="text-amber-500 w-5 h-5" />;
      default:
        return <FaTimesCircle className="text-red-400 w-5 h-5" />;
    }
  };

  const estadoBg = (estado) => {
    switch (estado) {
      case "PAGADO":
        return "bg-green-100 dark:bg-green-900/25 border-l-4 border-green-500";
      case "PARCIAL":
        return "bg-amber-100 dark:bg-amber-900/25 border-l-4 border-amber-500";
      default:
        return "";
    }
  };

  return (
    <div className="px-4 md:px-6 py-4 space-y-4">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Cobros</h1>

      {/* Mes / Anio selector */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
        <div className="grid grid-cols-2 sm:flex gap-2">
          <select
            value={mes}
            onChange={(e) => setMes(Number(e.target.value))}
            className="w-full sm:w-auto text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
          >
            {MESES.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
            className="w-full sm:w-auto text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
          >
            {[2024, 2025, 2026, 2027].map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:ml-auto gap-2">
          <button
            onClick={() => setAgrupar((v) => !v)}
            className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm border font-medium px-3 py-2 rounded-lg transition-colors whitespace-nowrap ${
              agrupar
                ? "bg-primary/10 border-primary text-primary"
                : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
            title="Agrupar los contratos por locador"
          >
            {agrupar ? "Desagrupar" : "Agrupar por locador"}
          </button>
          <button
            onClick={() => setShowExtras((v) => !v)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
            title={showExtras ? "Ocultar columnas extra" : "Mostrar columnas extra"}
          >
            {showExtras ? "Ocultar extras" : "Mostrar extras"}
          </button>
          <button
            onClick={handleExportarExcel}
            disabled={filtered.length === 0}
            className="col-span-2 sm:col-span-1 w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            title={`Exportar ${filtered.length} contrato${filtered.length === 1 ? "" : "s"} a Excel`}
          >
            <FaFileExcel className="w-4 h-4" />
            Exportar Excel ({filtered.length})
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-2xl font-bold text-green-500">{totales.pagados}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Pagados</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-2xl font-bold text-amber-500">{totales.parciales}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Parciales</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-2xl font-bold text-red-400">{totales.pendientes}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Pendientes</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-lg font-bold text-primary">
            {formatMonto(totales.montoCobrado)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            de {formatMonto(totales.montoEsperado)}
          </p>
        </div>
      </div>

      {/* Search + filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar locador, locatario o propiedad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2">
          {[
            { value: "TODOS", label: "Todos" },
            { value: "PENDIENTE", label: "Pendientes" },
            { value: "PARCIAL", label: "Parciales" },
            { value: "PAGADO", label: "Pagados" },
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setFiltroEstado(f.value)}
              className={`w-full sm:w-auto px-3 py-1.5 text-sm rounded-lg transition-colors whitespace-nowrap ${
                filtroEstado === f.value
                  ? "bg-primary text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <SortableHeader sortKey="estadoPago" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs w-8"> </SortableHeader>
                <SortableHeader sortKey="locatarioNombre" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs">Locatario</SortableHeader>
                <SortableHeader sortKey="locadorNombre" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs">Locador</SortableHeader>
                <SortableHeader sortKey="propiedad" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs">Propiedad</SortableHeader>
                <SortableHeader sortKey="montoActual" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="right" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs">Monto</SortableHeader>
                <SortableHeader sortKey="comisionPct" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs">%</SortableHeader>
                <SortableHeader sortKey="comisionMontoCalc" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="right" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs">ADM</SortableHeader>
                <SortableHeader sortKey="totalPropietario" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="right" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs">Total</SortableHeader>
                <SortableHeader sortKey="fechaVencimiento" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs">Vence</SortableHeader>
                <SortableHeader sortKey="proximoAumento" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs">Aumento</SortableHeader>
                <SortableHeader sortKey="totalGastos" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs">Gastos</SortableHeader>
                {showExtras && (
                  <>
                    <SortableHeader sortKey="montoProyectado" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="right" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs whitespace-nowrap">Ajustado</SortableHeader>
                    <SortableHeader sortKey="montoPagado" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="right" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs">Pagado</SortableHeader>
                    <SortableHeader sortKey="fechaPago" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-2 py-2 font-semibold text-gray-600 dark:text-gray-400 text-xs whitespace-nowrap">F. Pago</SortableHeader>
                  </>
                )}
                <th className="text-center px-3 py-3 font-semibold text-gray-600 dark:text-gray-400">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan={showExtras ? 15 : 12} className="px-4 py-8 text-center text-gray-400">
                    No hay contratos para este periodo
                  </td>
                </tr>
              ) : (
                filasRender.map((f) => {
                  if (f.tipo === "grupo") {
                    return (
                      <tr key={`grupo-${f.locador}`} className="bg-gray-100 dark:bg-gray-700/50 border-y border-gray-200 dark:border-gray-600">
                        <td colSpan={showExtras ? 15 : 12} className="px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          {f.locador || "—"}
                          <span className="ml-2 text-xs font-normal text-gray-500">
                            {f.items.length} contrato{f.items.length === 1 ? "" : "s"} · Total {formatMonto(f.subtotalMonto)} · Cobrado {formatMonto(f.subtotalCobrado)}
                          </span>
                        </td>
                      </tr>
                    );
                  }
                  const c = f.c;
                  return (
                  <tr
                    key={c.idFirebase}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${estadoBg(c.estadoPago)}`}
                  >
                    <td className="px-2 py-2 text-center">
                      {estadoIcon(c.estadoPago)}
                    </td>
                    <td className="px-2 py-2 text-sm">
                      <p className="font-medium text-gray-800 dark:text-gray-200">{c.locatarioNombre}</p>
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-600 dark:text-gray-400">{c.locadorNombre}</td>
                    <td className="px-2 py-2 text-sm text-gray-600 dark:text-gray-400">{c.propiedad}</td>
                    <td className="px-2 py-2 text-right text-sm font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">
                      {formatMonto(c.montoActual, c.moneda)}
                      {c.montoEsHistorico && (
                        <span
                          className="ml-1 text-[10px] px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 align-middle"
                          title={`Monto vigente en ${MESES[mes - 1]} ${anio}. Hoy el contrato esta en ${formatMonto(c.montoContratoActual, c.moneda)}.`}
                        >
                          hist.
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center text-sm text-gray-600 dark:text-gray-400">
                      {c.comisionPct}%
                    </td>
                    <td className="px-2 py-2 text-right text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {formatMonto(c.comisionMontoCalc, c.moneda)}
                    </td>
                    <td className="px-2 py-2 text-right text-sm font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">
                      {formatMonto(c.totalPropietario, c.moneda)}
                    </td>
                    <td className="px-2 py-2 text-center text-sm whitespace-nowrap">
                      {c.fechaVencimiento ? (
                        <div>
                          <span
                            className={`inline-block px-1.5 py-0.5 rounded ${getVencimientoClasses(c.venceEstado)}`}
                            title={
                              c.diasVence === null
                                ? ""
                                : c.diasVence < 0
                                ? `Vencido hace ${Math.abs(c.diasVence)} días`
                                : `En ${c.diasVence} días`
                            }
                          >
                            {fmtDate(c.fechaVencimiento)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center text-sm whitespace-nowrap">
                      {c.aumento?.proximoAumento ? (
                        <div>
                          <span
                            className={`text-gray-700 dark:text-gray-300 ${
                              c.diasAumento !== null && c.diasAumento <= 30 ? "font-medium" : ""
                            }`}
                          >
                            {fmtDate(c.aumento.proximoAumento)}
                          </span>
                          <span className="block text-[10px] text-gray-400 mt-0.5">
                            {c.aumento.tipo || ""}
                            {c.aumento.frecuenciaMeses ? ` x${c.aumento.frecuenciaMeses}` : ""}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">
                      {c.totalGastos > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-500" title={c.gastosContrato.map(g => g.descripcion).join(", ")}>
                          <FaWrench className="w-3 h-3" /> -{formatMonto(c.totalGastos, c.moneda)}
                        </span>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600 text-xs">-</span>
                      )}
                    </td>
                    {showExtras && (
                      <>
                        <td className="px-2 py-2 text-right text-sm whitespace-nowrap">
                          {c.montoProyectado ? (
                            <span
                              className={`font-medium ${c.proyeccionAprox ? "text-amber-600 dark:text-amber-400" : "text-blue-600 dark:text-blue-400"}`}
                              title={`Factor x${c.factorProyectado.toFixed(4)} hasta ${c.aumento?.proximoAumento || "-"}${c.proyeccionAprox ? " (aproximado, falta IPC del último mes)" : ""}`}
                            >
                              <FaArrowUp className="inline w-2.5 h-2.5 mr-0.5" />
                              {formatMonto(c.montoProyectado, c.moneda)}
                              {c.proyeccionAprox && <span className="ml-0.5">~</span>}
                            </span>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-600" title="Sin índices cargados o sin próximo aumento">
                              —
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right text-sm whitespace-nowrap">
                          {c.pago ? (
                            <span className={`font-medium ${
                              c.estadoPago === "PAGADO"
                                ? "text-green-600 dark:text-green-400"
                                : "text-amber-600 dark:text-amber-400"
                            }`}>
                              {formatMonto(c.pago.montoPagado, c.moneda)}
                            </span>
                          ) : (
                            <span className="text-red-400">-</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-center text-gray-500 dark:text-gray-400 text-sm whitespace-nowrap">
                          {c.pago?.fechaPago || "-"}
                        </td>
                      </>
                    )}
                    <td className="px-2 py-2 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-0.5">
                        <button
                          onClick={() => openRegistrarPago(c)}
                          className={`inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${
                            c.estadoPago === "PAGADO"
                              ? "bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600"
                              : "bg-primary hover:bg-secondary text-white"
                          }`}
                          title={c.estadoPago === "PAGADO" ? "Editar pago" : "Registrar pago"}
                        >
                          <FaMoneyBillWave className="w-3 h-3" />
                        </button>
                        {c.pdfContrato?.url && (
                          <a
                            href={c.pdfContrato.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-red-400 hover:text-red-600 transition-colors p-1"
                            title={`Ver PDF (${c.pdfContrato.name || "contrato.pdf"})`}
                          >
                            <FaFilePdf className="w-3 h-3" />
                          </a>
                        )}
                        {c.pago && (
                          <button
                            onClick={() => setReciboData(buildRecibo(c, c.pago))}
                            className="text-gray-400 hover:text-emerald-500 transition-colors p-1"
                            title="Enviar recibo por WhatsApp"
                          >
                            <FaReceipt className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          onClick={() => openHistorial(c)}
                          className="text-gray-400 hover:text-primary transition-colors p-1"
                          title="Ver historial"
                        >
                          <FaHistory className="w-3 h-3" />
                        </button>
                        {c.pago && (
                          <button
                            onClick={() => handleAnularPago(c.pago.idFirebase)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                            title="Anular pago"
                          >
                            <FaTimes className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-400 text-right">
        {filtered.length} de {contratosConPago.length} contratos | {MESES[mes - 1]} {anio}
      </p>

      {/* Modal Registrar Pago */}
      {modalContrato && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-xl mx-4 p-8">
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                {modalContrato.pago ? "Editar Pago" : "Registrar Pago"}
              </h3>
              <button onClick={() => setModalContratoId(null)} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <FaTimes />
              </button>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4 space-y-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {modalContrato.locatarioNombre}
              </p>
              <p className="text-xs text-gray-500">{modalContrato.propiedad} | Locador: {modalContrato.locadorNombre}</p>
              <p className="text-xs text-gray-400">{MESES[mes - 1]} {anio}</p>
              <div className="pt-1 space-y-0.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Alquiler:</span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">{formatMonto(modalContrato.montoActual, modalContrato.moneda)}</span>
                </div>
                {modalContrato.totalGastos > 0 && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-red-500">Gastos:</span>
                      <span className="font-semibold text-red-500">-{formatMonto(modalContrato.totalGastos, modalContrato.moneda)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-gray-200 dark:border-gray-600 pt-1">
                      <span className="font-medium text-gray-700 dark:text-gray-300">A cobrar:</span>
                      <span className="font-bold text-green-600 dark:text-green-400">{formatMonto(montoNetoModal, modalContrato.moneda)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Gastos de esta propiedad este mes */}
            {modalContrato.gastosContrato?.length > 0 && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/10 rounded-lg p-3">
                <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-2">
                  Gastos registrados este mes:
                </p>
                {modalContrato.gastosContrato.map((g) => (
                  <div key={g.idFirebase} className="flex items-center justify-between text-xs py-1">
                    <span className="text-red-600 dark:text-red-400">{g.descripcion} ({g.categoria})</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-red-600 dark:text-red-400">-{formatMonto(g.monto)}</span>
                      <button onClick={() => handleEliminarGasto(g.idFirebase)} className="text-red-300 hover:text-red-500">
                        <FaTrash className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between text-xs font-bold text-red-700 dark:text-red-400 pt-1 border-t border-red-200 dark:border-red-800 mt-1">
                  <span>Total gastos</span>
                  <span>-{formatMonto(modalContrato.totalGastos)}</span>
                </div>
              </div>
            )}

            {/* Agregar gasto inline */}
            {!showGastoForm ? (
              <button
                onClick={() => setShowGastoForm(true)}
                className="w-full mb-4 inline-flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:text-primary border border-dashed border-gray-300 dark:border-gray-600 rounded-lg py-2 hover:border-primary transition-colors"
              >
                <FaWrench className="w-3 h-3" /> Agregar gasto (plomero, electricista, etc.)
              </button>
            ) : (
              <div className="mb-4 bg-amber-50 dark:bg-amber-900/10 rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400">Nuevo gasto (se descuenta al propietario)</p>
                <input
                  type="text"
                  value={gastoForm.descripcion}
                  onChange={(e) => setGastoForm({ ...gastoForm, descripcion: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                  placeholder="Descripcion (ej: Plomero - arreglo canilla)"
                />
                <div className="flex gap-2">
                  <select
                    value={gastoForm.categoria}
                    onChange={(e) => setGastoForm({ ...gastoForm, categoria: e.target.value })}
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                  >
                    {CATEGORIAS.map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                  <MoneyInput
                    value={gastoForm.monto}
                    onChange={(v) => setGastoForm({ ...gastoForm, monto: v })}
                    className="w-28 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    placeholder="Monto"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowGastoForm(false)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">Cancelar</button>
                  <button
                    onClick={handleAgregarGasto}
                    disabled={!gastoForm.descripcion || !gastoForm.monto || saving}
                    className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-3 py-1 rounded-lg disabled:opacity-50"
                  >
                    Agregar
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Monto Pagado *
                </label>
                <MoneyInput
                  value={pagoForm.montoPagado}
                  onChange={(v) => setPagoForm({ ...pagoForm, montoPagado: v })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {Number(pagoForm.montoPagado) > 0 && Number(pagoForm.montoPagado) < montoNetoModal && (
                  <p className="text-xs text-amber-500 mt-1">
                    Pago parcial - Debe: {formatMonto(montoNetoModal - Number(pagoForm.montoPagado), modalContrato.moneda)}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Observaciones
                </label>
                <input
                  type="text"
                  value={pagoForm.observaciones}
                  onChange={(e) => setPagoForm({ ...pagoForm, observaciones: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Ej: Pago en efectivo, transferencia, etc."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => setModalContratoId(null)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRegistrarPago}
                  disabled={saving || !pagoForm.montoPagado}
                  className="px-4 py-2 text-sm bg-green-500 hover:bg-green-600 text-white rounded-lg disabled:opacity-50"
                >
                  {saving ? "Guardando..." : "Confirmar Pago"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historial */}
      {modalHistorial && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 p-8">
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                Historial de Pagos
              </h3>
              <button onClick={() => setModalHistorial(null)} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <FaTimes />
              </button>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {modalHistorial.contrato.locatarioNombre}
              </p>
              <p className="text-xs text-gray-500">
                {modalHistorial.contrato.propiedad} | Locador: {modalHistorial.contrato.locadorNombre}
              </p>
            </div>

            {modalHistorial.historial.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">
                No hay pagos registrados para este contrato
              </p>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0">
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                      <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Periodo</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Esperado</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Pagado</th>
                      <th className="text-center px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Fecha</th>
                      <th className="text-center px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {modalHistorial.historial.map((p) => {
                      const completo = Number(p.montoPagado) >= Number(p.montoEsperado);
                      return (
                        <tr key={p.idFirebase}>
                          <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                            {MESES[p.mes - 1]} {p.anio}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-500">
                            {formatMonto(p.montoEsperado, p.moneda)}
                          </td>
                          <td className={`px-3 py-2 text-right font-medium ${
                            completo ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"
                          }`}>
                            {formatMonto(p.montoPagado, p.moneda)}
                          </td>
                          <td className="px-3 py-2 text-center text-xs text-gray-500">{p.fechaPago}</td>
                          <td className="px-3 py-2 text-center">
                            {completo ? (
                              <FaCheckCircle className="text-green-500 w-4 h-4 mx-auto" />
                            ) : (
                              <FaExclamationCircle className="text-amber-500 w-4 h-4 mx-auto" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setModalHistorial(null)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recibo de cobro para compartir por WhatsApp */}
      {reciboData && (
        <ReciboShareModal data={reciboData} onClose={() => setReciboData(null)} />
      )}
    </div>
  );
}

export default Cobros;
